import React from 'react';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getPreviewTitle, getSafePreviewFallbackText } from '../../../utils/previewHelpers';
import { filterTypeRegistry } from '@intelligence/v3/filterCore/registry';

import type { IFilterGroup, IFilterItemConfig } from '@intelligence/v3/filterCore/types';

export const getItemFields = (items: IFilterItemConfig[]): string[] => {
  return items.map((item) => item.field);
};

export const getGroupedFieldSet = (filterGroups: IFilterGroup[]): Set<string> => {
  const groupedFieldSet = new Set<string>();

  filterGroups.forEach((group) => {
    (group.filterKeys || []).forEach((field) => {
      groupedFieldSet.add(field);
    });
  });

  return groupedFieldSet;
};

export const renderFieldNodes = (
  fields: string[],
  renderedItemMap: Map<string, React.ReactNode>,
): Array<React.ReactNode | undefined> => {
  return fields.map((field) => {
    return renderedItemMap.get(field);
  });
};

export const shouldRunFilterGroupDiagnostics = (): boolean => {
  return process.env.NODE_ENV !== 'production';
};

interface IFilterGroupDiagnostics {
  duplicateFields: string[];
  unknownFields: string[];
  ungroupedFields: string[];
}

export const getFilterGroupDiagnostics = (params: {
  items: IFilterItemConfig[];
  visibleItems: IFilterItemConfig[];
  filterGroups: IFilterGroup[];
}): IFilterGroupDiagnostics | null => {
  const { items, visibleItems, filterGroups } = params;

  if (filterGroups.length === 0) {
    return null;
  }

  const allFieldSet = new Set(items.map((item) => item.field));
  const groupedFieldSet = new Set<string>();
  const duplicateFields = new Set<string>();
  const unknownFields = new Set<string>();

  filterGroups.forEach((group) => {
    (group.filterKeys || []).forEach((field) => {
      if (groupedFieldSet.has(field)) {
        duplicateFields.add(field);
      }

      groupedFieldSet.add(field);

      if (!allFieldSet.has(field)) {
        unknownFields.add(field);
      }
    });
  });

  const ungroupedFields = visibleItems.filter((item) => !groupedFieldSet.has(item.field)).map((item) => item.field);
  const result = {
    duplicateFields: Array.from(duplicateFields),
    unknownFields: Array.from(unknownFields),
    ungroupedFields,
  };

  if (result.duplicateFields.length === 0 && result.unknownFields.length === 0 && result.ungroupedFields.length === 0) {
    return null;
  }

  return result;
};

interface IStickySummaryItem {
  key: string;
  content: React.ReactNode;
}

const DEFAULT_COLLAPSED_OVERFLOW_THRESHOLD = 60;
const FIRST_ROW_OFFSET_TOLERANCE = 1;
const COLLAPSED_OVERFLOW_THRESHOLD_CSS_VAR = '--dbi-filter-collapsed-overflow-threshold';

const getCollapsedOverflowThresholdFallback = (element: HTMLElement | null): number => {
  if (!element || typeof window === 'undefined') {
    return DEFAULT_COLLAPSED_OVERFLOW_THRESHOLD;
  }

  const rawThreshold = window.getComputedStyle(element).getPropertyValue(COLLAPSED_OVERFLOW_THRESHOLD_CSS_VAR).trim();
  const parsedThreshold = Number.parseFloat(rawThreshold);

  return Number.isFinite(parsedThreshold) && parsedThreshold > 0
    ? parsedThreshold
    : DEFAULT_COLLAPSED_OVERFLOW_THRESHOLD;
};

const getCollapsedRowItemElements = (element: HTMLElement): HTMLElement[] => {
  return Array.from(element.querySelectorAll('.dbi-filter-group__content > *')).filter((node): node is HTMLElement => {
    return node instanceof HTMLElement && node.offsetHeight > 0;
  });
};

export const getCollapsedOverflowThreshold = (element: HTMLElement | null): number => {
  if (!element) {
    return getCollapsedOverflowThresholdFallback(element);
  }

  const rowItemElements = getCollapsedRowItemElements(element);

  if (rowItemElements.length === 0) {
    return getCollapsedOverflowThresholdFallback(element);
  }

  const containerTop = element.getBoundingClientRect().top;
  const firstRowTop = Math.min(
    ...rowItemElements.map((itemElement) => {
      return itemElement.getBoundingClientRect().top;
    }),
  );
  const firstRowBottom = rowItemElements.reduce((currentBottom, itemElement) => {
    const rect = itemElement.getBoundingClientRect();

    if (Math.abs(rect.top - firstRowTop) > FIRST_ROW_OFFSET_TOLERANCE) {
      return currentBottom;
    }

    return Math.max(currentBottom, rect.bottom);
  }, firstRowTop);
  const measuredThreshold = firstRowBottom - containerTop;

  return measuredThreshold > 0 ? measuredThreshold : getCollapsedOverflowThresholdFallback(element);
};

export const getCollapsedHasOverflow = (element: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  const rowItemElements = getCollapsedRowItemElements(element);

  if (rowItemElements.length === 0) {
    return element.scrollHeight > getCollapsedOverflowThresholdFallback(element);
  }

  const firstRowTop = Math.min(
    ...rowItemElements.map((itemElement) => {
      return itemElement.getBoundingClientRect().top;
    }),
  );

  return rowItemElements.some((itemElement) => {
    const rect = itemElement.getBoundingClientRect();

    return rect.top - firstRowTop > FIRST_ROW_OFFSET_TOLERANCE;
  });
};

export const buildStickySummaryItems = (params: {
  useSticky: boolean;
  items: IFilterItemConfig[];
  values: IRecord;
  formatMessage: ({ id }: { id: string }) => string;
  previewCtx: {
    dataSourceOptions?: any[];
    platformOptions?: any[];
  };
}): IStickySummaryItem[] => {
  const { useSticky, items, values, formatMessage, previewCtx } = params;

  if (!useSticky) {
    return [];
  }

  return items.map((item) => {
    const currentValue = values[item.field];
    const filterEntry = filterTypeRegistry.getResolved(item.type, previewCtx);
    const fallbackValue = isEmpty(currentValue)
      ? formatMessage({ id: 'dbi_filterSave_none' })
      : getSafePreviewFallbackText(currentValue, formatMessage({ id: 'dbi_filterSave_none' }));

    return {
      key: item.field,
      content: filterEntry?.renderPreview?.(item, currentValue) || (
        <>
          <span className="model-form-text">{getPreviewTitle(item, formatMessage)}</span>
          <span className="model-form-context">{fallbackValue}</span>
        </>
      ),
    };
  });
};

export const resolveStickyPortalTarget = (
  stickyPortalContainer: HTMLElement | string | (() => HTMLElement | null) | null | undefined,
): HTMLElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const defaultTarget = document.body;

  if (typeof stickyPortalContainer === 'function') {
    return stickyPortalContainer() || defaultTarget;
  }

  if (typeof stickyPortalContainer === 'string') {
    return (document.querySelector(stickyPortalContainer) as HTMLElement | null) || defaultTarget;
  }

  return stickyPortalContainer || defaultTarget;
};

export const getStickyPortalTargetState = (
  stickyPortalContainer: HTMLElement | string | (() => HTMLElement | null) | null | undefined,
): {
  target: HTMLElement | null;
  shouldObserve: boolean;
} => {
  if (typeof document === 'undefined') {
    return {
      target: null,
      shouldObserve: false,
    };
  }

  const defaultTarget = document.body;

  if (typeof stickyPortalContainer === 'string') {
    const resolvedTarget = document.querySelector(stickyPortalContainer) as HTMLElement | null;

    return {
      target: resolvedTarget || defaultTarget,
      shouldObserve: resolvedTarget === null,
    };
  }

  if (typeof stickyPortalContainer === 'function') {
    const resolvedTarget = stickyPortalContainer();

    return {
      target: resolvedTarget || defaultTarget,
      shouldObserve: resolvedTarget === null,
    };
  }

  return {
    target: resolveStickyPortalTarget(stickyPortalContainer),
    shouldObserve: false,
  };
};
